
import React, { useState, useEffect } from 'react';
import { 
  Bell, Check, X, Flame, Trophy, 
  Users, Zap, Info, Clock, Trash2, 
  ChevronRight, Sparkles, Settings as SettingsIcon
} from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { User, NotificationItem } from '../types';

export const NotificationCenter = ({ user, authReady, onClose, setView, t }: { user: User, authReady: boolean, onClose: () => void, setView: (v: any) => void, t?: any }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // 🔒 CRITICAL FIX: Verify user.id === auth.currentUser.uid BEFORE creating listener
    const currentUid = auth.currentUser?.uid;
    if (!currentUid || currentUid !== user.id) {
      if (import.meta.env.DEV) {
        console.warn('[NOTIFICATIONS] NotificationCenter blocked: user.id !== currentUser.uid');
      }
      setNotifications([]);
      return;
    }

    console.log('[NOTIFICATIONS] Setting up notifications query for user:', user.id);
    const q = query(
      collection(db, "users", user.id, "notifications"),
      orderBy("createdAt", "desc")
    );
    
    // CRITICAL: Initialize as null, assign only if onSnapshot returns a function
    let unsubscribe: (() => void) | null = null;
    
    try {
      const snapshotResult = onSnapshot(q, (snap) => {
        console.log('[NOTIFICATIONS] Firestore snapshot received:', snap.size, 'notifications');
        setNotifications(snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title || '',
            body: data.body || data.message || '',
            type: data.type,
            read: data.read ?? data.isRead ?? false,
            createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : (typeof data.createdAt === 'number' ? data.createdAt : Date.now())
          } as NotificationItem;
        }));
      }, (error) => {
        // CRITICAL: Handle permission-denied errors gracefully - don't crash the UI
        if (error.code === 'permission-denied') {
          if (import.meta.env.DEV) {
            console.warn('[NOTIFICATIONS] Permission denied for notifications - user may not have access');
          }
          setNotifications([]); // Set empty array - UI shows empty notifications instead of crashing
        } else {
          if (import.meta.env.DEV) {
            console.error('[NOTIFICATIONS] ❌ Error loading notifications:', {
              code: error.code,
              message: error.message,
              userId: user.id
            });
          }
          setNotifications([]); // Set empty array on any error
        }
      });
      if (typeof snapshotResult === 'function') {
        unsubscribe = snapshotResult;
      }
    } catch (initError: any) {
      if (import.meta.env.DEV) {
        console.error('[NOTIFICATIONS] Error initializing snapshot listener:', initError);
      }
    }
    
    return () => {
      if (import.meta.env.DEV) {
        console.log('[NOTIFICATIONS] Cleaning up notifications subscription');
      }
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [authReady, user.id]);

  const markAsRead = async (id: string) => {
    await updateDoc(doc(db, "users", user.id, "notifications", id), { read: true });
  };

  const markAllAsRead = async () => {
    const batch = writeBatch(db);
    notifications.filter(n => !n.read).forEach(n => {
      batch.update(doc(db, "users", user.id, "notifications", n.id), { read: true });
    });
    await batch.commit();
  };

  const deleteNotification = async (id: string) => {
    await deleteDoc(doc(db, "users", user.id, "notifications", id));
  };

  const filtered = activeFilter === 'unread' ? notifications.filter(n => !n.read) : notifications;

  return (
    <div className="flex flex-col h-full bg-slate-950 border-l border-slate-900 shadow-2xl animate-slideInRight w-full max-w-md ml-auto">
      {/* Header */}
      <div className="p-6 border-b border-slate-900 flex justify-between items-center bg-slate-950/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/10 rounded-xl">
            <Bell className="text-emerald-500" size={20}/>
          </div>
          <div>
            <h2 className="font-black text-lg uppercase tracking-tight">{t?.notif_center || 'Notifications'}</h2>
            <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">
              {notifications.filter(n => !n.read).length} {t?.notif_unread || 'Unread'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <button onClick={() => { setView('settings'); onClose(); }} className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors">
             <SettingsIcon size={20}/>
           </button>
           <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-xl text-slate-400">
             <X size={20}/>
           </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex p-4 gap-2 border-b border-slate-900 bg-slate-950">
        <button 
          // Fixed: changed setActiveTab to setActiveFilter to match defined state hook
          onClick={() => setActiveFilter('all')} 
          className={`flex-1 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeFilter === 'all' ? 'bg-emerald-500 text-white shadow-lg' : 'bg-slate-900 border border-slate-800 text-slate-500'}`}
        >
          {t?.notif_all || 'All'}
        </button>
        <button 
          onClick={() => setActiveFilter('unread')} 
          className={`flex-1 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeFilter === 'unread' ? 'bg-emerald-500 text-white shadow-lg' : 'bg-slate-900 border border-slate-800 text-slate-500'}`}
        >
          {t?.notif_unread || 'Unread'}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
        {notifications.length > 0 && (
          <button 
            onClick={markAllAsRead}
            className="w-full py-3 text-[10px] font-black uppercase text-emerald-500 hover:text-emerald-400 transition-colors tracking-[0.2em]"
          >
            {t?.notif_mark_all_read || 'Mark all as read'}
          </button>
        )}

        {filtered.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center opacity-30 space-y-4 py-20">
             <div className="w-20 h-20 bg-slate-900 border border-slate-800 rounded-full flex items-center justify-center">
                <Bell size={40}/>
             </div>
             <p className="font-black text-xs uppercase tracking-widest">{t?.notif_no_notifications || 'All quiet here...'}</p>
          </div>
        ) : (
          filtered.map(notif => (
            <div 
              key={notif.id} 
              onClick={() => markAsRead(notif.id)}
              className={`group relative p-4 rounded-[1.5rem] border transition-all cursor-pointer ${
                notif.read 
                  ? 'bg-slate-900/30 border-slate-800' 
                  : 'bg-slate-900 border-emerald-500/30 shadow-lg shadow-emerald-500/5'
              }`}
            >
              {!notif.read && (
                <div className="absolute top-4 right-4 w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              )}
              
              <div className="flex gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                  notif.type === 'motivational' ? 'bg-orange-500/10 text-orange-500' :
                  notif.type === 'social' ? 'bg-blue-500/10 text-blue-500' :
                  notif.type === 'challenge' ? 'bg-purple-500/10 text-purple-500' :
                  notif.type === 'ai_insight' ? 'bg-cyan-500/10 text-cyan-400' :
                  'bg-emerald-500/10 text-emerald-500'
                }`}>
                  {getIcon(notif.type)}
                </div>
                <div className="flex-1 min-w-0 pr-6">
                  <h3 className={`text-sm font-black mb-1 truncate ${!notif.read ? 'text-white' : 'text-slate-400'}`}>
                    {notif.title}
                  </h3>
                  <p className="text-xs text-slate-500 font-medium leading-relaxed">
                    {notif.body}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <Clock size={10} className="text-slate-600"/>
                    <span className="text-[9px] font-black uppercase text-slate-600 tracking-tighter">
                      {formatTime(notif.createdAt, t)}
                    </span>
                  </div>
                </div>
              </div>

              <button 
                onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                className="absolute bottom-4 right-4 p-2 opacity-0 group-hover:opacity-100 text-slate-600 hover:text-red-400 transition-all"
              >
                <Trash2 size={14}/>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const getIcon = (type: string) => {
  switch(type) {
    case 'motivational': return <Flame size={20} fill="currentColor"/>;
    case 'social': return <Users size={20}/>;
    case 'challenge': return <Trophy size={20}/>;
    case 'ai_insight': return <Sparkles size={20}/>;
    default: return <Zap size={20} fill="currentColor"/>;
  }
};

const formatTime = (ts: number | any, t?: any) => {
  if (!ts) return t?.time_just_now || t?.notif_now || 'Adesso';
  const date = ts.toDate ? ts.toDate() : (typeof ts === 'number' ? new Date(ts) : new Date(ts));
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t?.time_just_now || t?.notif_now || 'Adesso';
  if (mins < 60) return (t?.time_minutes_ago || '{n}m fa').replace('{n}', mins.toString());
  const hours = Math.floor(mins / 60);
  if (hours < 24) return (t?.time_hours_ago || '{n}h fa').replace('{n}', hours.toString());
  return date.toLocaleDateString();
};
